const UOM_METER = "meter";
const UOM_ROLL = "roll";
const ORIGIN_PDP = "pdp";
const ORIGIN_CART = "cart";

const isCollectionPage = document.body.classList.contains("collection-template");
const isProductPage = document.body.classList.contains("product-template");

const selectors = {
  DROP_CALCULATOR: "cp-drop-calculator",
  DROP_GROUP: "data-drop-group",
  DROPS_QTY: "data-drops-qty",
  DROPS_LENGTH: "data-drops-length",
  DROPS_LINE_ITEM_PROP: "data-drops-line-item-prop",
  METERS_REMAINING: "data-meters-remaining",
  MAIN_PRODUCT_FORM: ".main-product-form",
};

/* Helpers / utils */
const isClickedOutside = (event, element) => {
  const elementDimensions = element.getBoundingClientRect();
  const isClickOutside = !(
    event.clientX > elementDimensions.left &&
    event.clientX < elementDimensions.right &&
    event.clientY > elementDimensions.top &&
    event.clientY < elementDimensions.bottom
  );

  return isClickOutside;
};

class CPCustomQuantity extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.productForm = this.closest("form");
    this.selectedUnitOfMeasure = this.getSelectedUnitOfMeasure();
    this.setAttribute("data-selected-unit-of-measure", this.selectedUnitOfMeasure);
    this.customQtyInput = this.productForm.querySelector(
      `cp-quantity-input[data-unit-of-measure='${this.selectedUnitOfMeasure}'] .quantity__input`,
    );
    this.defaultQuantityInput = this.closest("form")?.querySelector("quantity-input .quantity__input");
    this.customAtcButton = this.productForm.querySelector("button[data-custom-atc]");
    this.uomTabTriggers = this.productForm.querySelectorAll(".custom-qty-uom__radio");

    this.defaultQtyBuy = this.productForm.querySelector("div[data-default-qty-buy]");
    this.defaultAtcButton = this.productForm.querySelector("button[type='submit']");

    this.selectedVariantId = this.getSelectedVariantId();
    this.variantsData = JSON.parse(
      document.querySelector('script[type="application/json"][data-cp-variants]').textContent || [],
    );

    this.meterTabTrigger = this.querySelector("#custom-qty-uom-meter");
    this.rollTabTrigger = this.querySelector("#custom-qty-uom-roll");

    this.rollInput = this.querySelector("cp-quantity-input[data-unit-of-measure='roll'] .quantity__input");
    this.meterInput = this.querySelector("cp-quantity-input[data-unit-of-measure='meter'] .quantity__input");

    this.rollOnlyUi = this.hasAttribute("data-roll-only-ui");

    if (this.rollOnlyUi) {
      // if Roll only, need to update Default QTY input to min roll length
      this.defaultQuantityInput.value = this.dataset.rollLength;
    } else {
      if (this.selectedUnitOfMeasure === UOM_ROLL) {
        setTimeout(() => {
          this.defaultQuantityInput.value = Number(this.dataset.rollLength);
        });
      } else {
        setTimeout(() => {
          this.defaultQuantityInput.value = Number(this.dataset.minQty) * 10;
        });
      }
    }

    this.rollDisabled = this.hasAttribute("data-roll-disabled");

    if (this.rollDisabled) {
      this.toggleRollTabTrigger(false);
    } else {
      this.toggleRollTabTrigger(true);
    }

    this.uomTabTriggers.forEach((radio) => {
      radio.addEventListener("change", this.handleUnitOfMeasureChange.bind(this));
    });

    this.updateQtyLineItemAttribute(this.customQtyInput.value, this.selectedUnitOfMeasure);

    this.customAtcButton.addEventListener("click", () => {
      // Update Formdata prior to submit for parity
      const customValue = this.customQtyInput.value;
      if (this.selectedUnitOfMeasure === UOM_METER) {
        const targetValue = Number(customValue) * 10;
        this.defaultQuantityInput.value = targetValue;
      } else {
        const rollLength = Number(this.dataset.rollLength);
        const targetValue = Number(customValue) * rollLength;
        this.defaultQuantityInput.value = targetValue;
      }

      // Add or update unique ID to prevent items clashing
      const uniqueInput = this.productForm.querySelector("input[data-unique-id]");
      const custonInputMarkup = `<input type="hidden" name="properties[_uid]" value="${Date.now()}" data-unique-id>`;
      if (uniqueInput) {
        uniqueInput.value = Date.now();
      } else {
        this.productForm.insertAdjacentHTML("afterbegin", custonInputMarkup);
      }
      this.defaultAtcButton.click();
    });

    this.productForm.addEventListener(
      "submit",
      (event) => {
        // Intercept PDP ATC (when drop-calculator is enabled) & launch drop-calculator
        if (event.submitter?.hasAttribute("data-drop-calculator-enabled")) {
          const dropCalculator = document.querySelector(selectors.DROP_CALCULATOR);
          const isByTheMeter = this.selectedUnitOfMeasure === UOM_METER;
          if (dropCalculator && isByTheMeter) {
            event.preventDefault();
            event.stopPropagation();
            dropCalculator.init({ totalLength: this.customQtyInput.value, formToSubmit: this.productForm });
          }
        }
      },
      true,
    );

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
      this.handleVariantChange(variant);
    });

    // add mutation observer to listen form initialised: true prop on stock level indicator
    const stockIndicator = document.querySelector("cp-stock-indicator");
    if (stockIndicator) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList.contains("is-initialised")) {
            this.updateStockIndicator({
              unitOfMeasure: this.selectedUnitOfMeasure,
              variantId: this.selectedVariantId,
            });
            observer.disconnect();
          }
        });
      });
      observer.observe(stockIndicator, { attributes: true });
    }
  }

  getSelectedVariantId() {
    return this.productForm.querySelector("input[name='id']").value;
  }

  getSelectedUnitOfMeasure() {
    return this.productForm.querySelector("input[name='custom-qty-uom']:checked").value;
  }

  handleVariantChange(variant) {
    if (variant.available) {
      this.customAtcButton.removeAttribute("disabled");
    } else {
      this.customAtcButton.setAttribute("disabled", true);
    }

    this.resetDefaultQtyInput();
    this.resetCustomQtyInput(this.selectedUnitOfMeasure);

    if (this.variantsData) {
      const variantData = this.variantsData.find((v) => Number(v.id) === Number(variant.id));
      const inventoryQuantity = Number(variantData.inventory_quantity);
      const inventoryPolicyContinue = variantData.inventory_policy === "continue";

      if (inventoryPolicyContinue || Number(variantData.max_rolls) > 0) {
        this.toggleRollTabTrigger(true);
      } else {
        this.toggleRollTabTrigger(false);
      }

      if (inventoryPolicyContinue) {
        this.removeMaxLimits();
      } else {
        this.updateMaxRolls(variant.id);
        this.updateMaxMeters(inventoryQuantity);
      }

      if (this.selectedUnitOfMeasure === UOM_ROLL && !inventoryPolicyContinue) {
        const roll_length = Number(this.dataset.rollLength);
        if (roll_length > inventoryQuantity) {
          this.toggleRollTabTrigger(false);
        } else {
          this.toggleRollTabTrigger(true);
        }
      }
    }
    this.selectedVariantId = variant.id;
    this.updateStockIndicator({ unitOfMeasure: this.selectedUnitOfMeasure, variantId: this.selectedVariantId });
    this.updatePrice({ unitOfMeasure: this.selectedUnitOfMeasure });
  }

  removeMaxLimits() {
    this.rollInput?.removeAttribute("data-max");
    this.rollInput?.removeAttribute("max");
    this.rollInput?.parentElement.querySelector("button[name='plus']")?.removeAttribute("disabled");
    this.meterInput?.removeAttribute("data-max");
    this.meterInput?.removeAttribute("max");
    this.meterInput?.parentElement.querySelector("button[name='plus']")?.removeAttribute("disabled");
  }

  toggleRollTabTrigger(isVisible = false) {
    if (isVisible) {
      this.rollTabTrigger.removeAttribute("disabled");
    } else {
      this.rollTabTrigger.setAttribute("disabled", true);
      if (!this.rollOnlyUi) {
        this.meterTabTrigger.click();
      }
    }
  }

  disableRollInput(isDisabled) {
    this.rollInput.disabled = isDisabled;
  }

  updateMaxRolls(variantId) {
    const variantData = this.variantsData.find((v) => Number(v.id) === Number(variantId));
    const maxRolls = variantData.max_rolls;

    if (!this.rollInput) return;

    this.rollInput.setAttribute("data-max", maxRolls);
    this.rollInput.setAttribute("max", maxRolls);
    if (this.rollInput.value >= maxRolls) {
      this.rollInput.value = maxRolls;
      this.rollInput.parentElement.querySelector("button[name='plus']").setAttribute("disabled", true);
    } else {
      this.rollInput.parentElement.querySelector("button[name='plus']").removeAttribute("disabled");
    }
  }

  updateMaxMeters(inventoryQuantity) {
    const maxMeters = inventoryQuantity / 10;
    if (!this.meterInput) return;

    this.meterInput.setAttribute("data-max", maxMeters);
    this.meterInput.setAttribute("max", maxMeters);
    if (this.meterInput.value >= maxMeters) {
      this.meterInput.value = maxMeters;
      this.meterInput.parentElement.querySelector("button[name='plus']").setAttribute("disabled", true);
    } else {
      this.meterInput.parentElement.querySelector("button[name='plus']").removeAttribute("disabled");
    }
  }

  handleUnitOfMeasureChange(event) {
    const unitOfMeasure = event.target.value;
    this.selectedUnitOfMeasure = unitOfMeasure;
    const legendValue = this.productForm.querySelector("[data-uom-value]");
    const moqMessage = this.productForm.querySelector("[data-moq-message]");
    legendValue.textContent = unitOfMeasure;
    this.resetCustomQtyInput(unitOfMeasure);
    this.updatePrice({ unitOfMeasure: unitOfMeasure });
    this.toggleUnitsOfMeasureUI(unitOfMeasure);
    this.resetDefaultQtyInput();
    this.updateStockIndicator({ unitOfMeasure: this.selectedUnitOfMeasure, variantId: this.selectedVariantId });
    this.setAttribute("data-selected-unit-of-measure", this.selectedUnitOfMeasure);

    if (unitOfMeasure === UOM_METER) {
      if (moqMessage) moqMessage.style.display = "block";
      this.disableRollInput(true);
    } else {
      if (moqMessage) moqMessage.style.display = "none";
      this.disableRollInput(false);
    }

    this.updateQtyLineItemAttribute(this.customQtyInput.value, this.selectedUnitOfMeasure);
  }

  resetDefaultQtyInput() {
    const event = new Event("change", { bubbles: true });

    if (this.selectedUnitOfMeasure === UOM_METER) {
      this.defaultQuantityInput.value = this.dataset.minQty * 10;
    } else {
      this.rollLength = this.productForm
        .querySelector(`cp-quantity-input[data-unit-of-measure='${UOM_ROLL}']`)
        .getAttribute("data-roll-length");
      this.defaultQuantityInput.value = Number(this.rollLength);
    }

    this.updateQtyLineItemAttribute(this.minValue, this.selectedUnitOfMeasure);
    this.defaultQuantityInput.dispatchEvent(event);
  }

  resetCustomQtyInput(unitOfMeasure) {
    this.customQtyInput = this.productForm.querySelector(`[data-unit-of-measure='${unitOfMeasure}'] .quantity__input`);
    this.minValue = this.customQtyInput.getAttribute("data-min");
    this.customQtyInput.value = this.minValue;
  }

  toggleUnitsOfMeasureUI(unitOfMeasure) {
    if (unitOfMeasure === UOM_METER) {
      this.productForm.querySelector("cp-quantity-input[data-unit-of-measure='meter']").style.display = "flex";
      this.productForm.querySelector("cp-quantity-input[data-unit-of-measure='roll']").style.display = "none";
    } else {
      this.productForm.querySelector("cp-quantity-input[data-unit-of-measure='meter']").style.display = "none";
      this.productForm.querySelector("cp-quantity-input[data-unit-of-measure='roll']").style.display = "flex";
    }
  }

  updatePrice({ unitOfMeasure }) {
    const priceElement = document.querySelector("[data-cp-price]");
    const regularPriceTarget = priceElement.querySelector("[data-price-regular]");

    if (unitOfMeasure === UOM_METER) {
      regularPriceTarget.innerHTML =
        priceElement.dataset.oneMeterPrice + `<span data-price-unit-text>${priceElement.dataset.perMeterText}</span>`;
    } else {
      regularPriceTarget.innerHTML =
        priceElement.dataset.rollPrice + `<span data-price-unit-text>${priceElement.dataset.rollText}</span>`;
    }
  }

  updateQtyLineItemAttribute(value, unitOfMeasure) {
    this.productForm.querySelector("input[data-total-length]")?.remove();
    this.productForm.querySelector("input[data-roll-length]")?.remove();
    this.productForm.insertAdjacentHTML("afterbegin", this.getCustomAttributeMarkup(value, unitOfMeasure));
  }

  getCustomAttributeMarkup(value, unitOfMeasure) {
    if (unitOfMeasure === UOM_METER) {
      return `
        <input id="total-length" type="hidden" name="properties[Meters]" value="${value}m" data-total-length>
      `;
    } else {
      return `
        <input id="total-length" type="hidden" name="properties[Rolls]" value="${value}" data-total-length>
        <input id="roll-length" type="hidden" name="properties[_roll-length]" value="${this.dataset.rollLength}" data-roll-length>
      `;
    }
  }

  updateStockIndicator({ unitOfMeasure, variantId }) {
    const stockIndicator = document.querySelector("cp-stock-indicator.is-initialised");
    if (stockIndicator) {
      stockIndicator.updateStockIndicator({ unitOfMeasure, variantId });
    }
  }
}

customElements.define("cp-custom-quantity", CPCustomQuantity);

/* 
  This is a custom QTY input element. It is used only for presentational purposes.
  This element passes data to Default QTY input (which is hidden) and that in-turn handles logic.
  This input is used on PDP & Cart Drawer. Inside cart-drawer it updates QTYs and attributes via custom fetch.
*/
class CPQuantityInput extends QuantityInput {
  constructor() {
    super();
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.origin = this.getAttribute("data-origin");
    this.defaultQuantityInput = this.closest("form")?.querySelector("quantity-input .quantity__input");
    this.buttons = Array.from(this.querySelectorAll("button"));
    this.changeEvent = new Event("change", { bubbles: true });
    this.debouncedUpdateQtyInCart = FoxTheme.utils.debounce(this.updateQtyInCart.bind(this), 300);
    this.input.addEventListener("change", this.onInputChange.bind(this), { signal: this.abortController.signal });
    this.buttons.forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this), { signal: this.abortController.signal }),
    );
    this.productForm = this.closest("form");
    this.unitOfMeasure = this.getAttribute("data-unit-of-measure");
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === "plus") {
      this.input.stepUp();
      this.updateLineItemAttField(this.input.value);
    } else {
      this.input.stepDown();
      this.updateLineItemAttField(this.input.value);
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.getAttribute("data-min") === previousValue && event.currentTarget.name === "minus") {
      this.input.value = parseFloat(this.input.min);
      this.updateLineItemAttField(this.input.value);
    }
  }

  updateLineItemAttField(value) {
    if (this.origin === ORIGIN_CART) return;
    const lineItemAttField = this.productForm.querySelector("input[data-total-length]");
    if (lineItemAttField) {
      lineItemAttField.value = this.unitOfMeasure === UOM_METER ? `${value}m` : `${value}`;
    } else {
      console.error("Meters input not found (line item att field)");
    }
  }

  handlePDPInputChange() {
    if (this.unitOfMeasure === UOM_METER) {
      this.defaultQuantityInput.value = parseInt(this.input.value * 10);
    } else {
      this.rollLength = this.dataset.rollLength;
      this.defaultQuantityInput.value = parseInt(this.input.value * this.rollLength);
    }
    this.updateLineItemAttField(this.input.value);
  }

  handleCartInputChange() {
    let actualQty = 0;
    let lineItemPropValue = "";
    let sectionsToBundle = this.getSectionsToBundle();
    let properties = {};
    let qtyInputName = "";
    if (this.unitOfMeasure === UOM_METER) {
      actualQty = this.input.value * 10;
      lineItemPropValue = `${parseFloat(this.input.value).toFixed(1)}m`;
      properties = {
        Meters: lineItemPropValue,
      };
      qtyInputName = "cp-qty-meter";
    } else {
      this.rollLength = this.dataset.rollLength;
      actualQty = this.input.value * this.rollLength;
      lineItemPropValue = Number(this.input.value);
      properties = {
        Rolls: lineItemPropValue,
        "_roll-length": this.rollLength,
      };
      qtyInputName = "cp-qty-roll";
    }

    this.debouncedUpdateQtyInCart({
      line: this.dataset.lineItemIndex,
      quantity: actualQty,
      properties: properties,
      sections: sectionsToBundle,
      qtyInputName: qtyInputName,
    });
  }

  onInputChange() {
    if (!this.validateInputBounds()) return;

    if (this.origin === ORIGIN_PDP) {
      this.handlePDPInputChange();
    } else {
      this.handleCartInputChange();
    }

    this.validateQtyRules();
  }

  updateQtyInCart({ line, quantity, properties, sections, qtyInputName }) {
    if (!line || !quantity || !properties || !sections || !qtyInputName) {
      console.error("Missing required parameters for updateQtyInCart");
      return;
    }

    const body = JSON.stringify({
      line,
      quantity,
      properties,
      sections,
    });
    fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: parsedState,
          target: this,
          line,
          name: qtyInputName,
        });
      })
      .catch((error) => console.log(error));
  }

  getSectionsToBundle() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }),
    );
    return sectionsToBundle;
  }

  validateInputBounds() {
    // Validate against min boundry
    if (this.input.value === "" || Number(this.input.value) < Number(this.input.min)) {
      this.input.value = parseFloat(this.input.min);
      const event = new Event("change", { bubbles: true });
      this.input.dispatchEvent(event);
      return false;
    }
    // Validate against max boundry (skip when inventory_policy is 'continue' - no max limit)
    const max = this.input.getAttribute("max");
    if (max !== null && max !== "" && Number(this.input.value) > Number(max)) {
      this.input.value = parseFloat(max);
      const event = new Event("change", { bubbles: true });
      this.input.dispatchEvent(event);
      return false;
    }
    return true;
  }

  validateQtyRules() {
    const value = parseFloat(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute("disabled", parseFloat(value) <= parseFloat(this.input.min));
    }
    // Only enforce max / disable plus when max is set (inventory_policy != 'continue')
    const max = this.input.getAttribute("max");
    if (max !== null && max !== "") {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute("disabled", parseFloat(value) >= parseFloat(max));
    }
  }
}

customElements.define("cp-quantity-input", CPQuantityInput);

class CPDropCalculator extends HTMLElement {
  /* 
    Note: In some places, we use Math.round(x * 10) / 10 to ensure we have 1 decimal only.
    This is because we are dealing with decimal values and we want to avoid floating point precision issues.
    @TODO: Potentially change this approach to do all maths with integers and divide by 10 at the end. Shopify themselves use that approach to avoid floating point precision issues.
  */
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init(options = {}) {
    this.dialog = this.querySelector("dialog") || this.closest("dialog");
    this.dialogScrollableArea = this.dialog.querySelector("[data-modal-scrollable]");
    this.totalLength = parseFloat(options?.totalLength) ?? 0;
    this.formToSubmit = options?.formToSubmit ?? null;

    if (this.totalLength === 0 || !this.formToSubmit) {
      // total length required
      this.closeAndReset();
      return;
    }

    this.abortController = new AbortController();
    this.dropsContainer = this.querySelector("[data-drops-container]");
    this.dropsCount = this.dropsContainer.querySelectorAll(`[${selectors.DROP_GROUP}]`).length;
    this.closeButton = this.querySelector("[data-modal-close]");
    this.noDropsButton = this.querySelector("[data-btn-no-drops]");
    this.addDropsButton = this.querySelector("[data-btn-add-drops]");
    this.addDropButton = this.querySelector("[data-add-drop]");
    this.innerModal = this.querySelector("[data-drop-calculator-inner]");
    this.metersRemaining = this.querySelector("[data-meters-remaining]");
    this.maxDrops = Number(this.getAttribute("data-max-drops"));

    this.metersRemaining.textContent = this.totalLength + "m";
    this.addEventListeners();
    this.openDropCalculator();
  }

  addEventListeners() {
    if (this.closeButton && this.dialog) {
      this.closeButton.addEventListener("click", () => this.closeAndReset(), { signal: this.abortController.signal });
    }

    this.addEventListener(
      "click",
      (event) => {
        if (isClickedOutside(event, this.innerModal)) {
          this.closeAndReset();
        }
      },
      { signal: this.abortController.signal },
    );

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.closeAndReset.bind(this));

    this.noDropsButton.addEventListener("click", this.continueWithoutDrops.bind(this), {
      signal: this.abortController.signal,
    });

    this.addEventListener("input", (event) => {
      // early return if input irrelevant
      if (!event.target.hasAttribute(selectors.DROPS_QTY) && !event.target.hasAttribute(selectors.DROPS_LENGTH)) {
        return;
      }

      const value = parseFloat(event.target.value);
      // When drop length has excess decimals, remove the excess eg: 1.27 -> 1.2
      if (event.target.hasAttribute(selectors.DROPS_LENGTH)) {
        const valueHasExcessDecimals = value.toString().includes(".") && value.toString().split(".")[1].length > 1;
        if (valueHasExcessDecimals) {
          const stringValue = value.toString();
          const validValue = stringValue.split(".")[0] + "." + stringValue.split(".")[1].slice(0, 1);
          if (validValue !== event.target.value) {
            event.target.value = validValue;
          }
        }
      }

      // For dropw qty, only allow integer input
      if (event.target.hasAttribute(selectors.DROPS_QTY)) {
        const valueContainsDecimals = value.toString().includes(".");
        if (valueContainsDecimals) {
          event.target.value = event.target.value.replace(".", "");
        }
      }

      this.validateDrops(this.totalLength);
    });

    this.addDropButton.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.addDrop();
        }
      },
      { signal: this.abortController.signal },
    );
    this.addDropButton.addEventListener("click", this.addDrop.bind(this), { signal: this.abortController.signal });

    this.dropsContainer.addEventListener("click", (event) => {
      if (event.target.hasAttribute("data-remove-drop")) {
        const drop = event.target.closest(`[${selectors.DROP_GROUP}]`);
        drop.remove();
        this.updateRemoveDropButtons();
        this.validateDrops(this.totalLength);
      }
    });

    this.addDropsButton.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.formToSubmit.requestSubmit();
      },
      { signal: this.abortController.signal },
    );
  }

  addDrop() {
    const allDrops = this.dropsContainer.querySelectorAll(`[${selectors.DROP_GROUP}]`);

    if (allDrops.length >= this.maxDrops) {
      return;
    }

    const firstDrop = this.dropsContainer.querySelector(".drop-group");
    const drop = firstDrop.cloneNode(true);
    drop.querySelectorAll("input").forEach((input, index) => {
      input.value = "";
      input.id = `${input.id}-${index + 1}-${Date.now()}`;
    });
    drop.setAttribute("data-cloned-drop", "true");
    drop.setAttribute("data-drop-index", allDrops.length + 1);
    this.dropsContainer.insertAdjacentElement("beforeend", drop);
    this.updateRemoveDropButtons();
    this.validateDrops(this.totalLength);
    this.dropsContainer
      .querySelector(`[data-drop-index="${allDrops.length + 1}"]`)
      .querySelector(`[${selectors.DROPS_QTY}]`)
      .focus();
  }

  updateRemoveDropButtons() {
    const removeDropButtons = this.querySelectorAll("[data-remove-drop]");
    if (removeDropButtons.length > 1) {
      removeDropButtons.forEach((button) => {
        button.removeAttribute("disabled");
      });
    } else {
      const removeDropButton = this.querySelector("[data-remove-drop]");
      removeDropButton.setAttribute("disabled", "true");
    }
  }

  continueWithoutDrops() {
    this.deleteDropsLineItemProp();
    this.generateDropsLineItemProp(`1x${this.totalLength}m,`);

    // Submit the form
    return this.formToSubmit.dispatchEvent(
      new SubmitEvent("submit", { bubbles: true, composed: true, cancelable: true }),
    );
  }

  updateMetersRemainingLabel(params) {
    const { totalLength, dropsTotal } = params;
    const parentEl = this.metersRemaining.parentElement;

    if (!totalLength || typeof dropsTotal !== "number") {
      console.error("Missing required parameters for updateMetersRemainingLabel");
      return;
    }

    if (dropsTotal > totalLength) {
      parentEl.classList.add("error");
    } else {
      parentEl.classList.remove("error");
    }

    this.metersRemaining.textContent = `${Math.round((totalLength - dropsTotal) * 10) / 10}m`;
  }

  updateAddDropButton({ allDropsHaveValues, dropsTotal, totalLength }) {
    const dropsCount = this.dropsContainer.querySelectorAll(`[${selectors.DROP_GROUP}]`).length;
    /* Add Drop button clickable if: 
      - all drop inputs are filled AND
      - max drops not exceeded AND
      - drops length does not exceed added fabric length
    */
    if (allDropsHaveValues && dropsCount < this.maxDrops && dropsTotal < totalLength) {
      this.addDropButton.removeAttribute("disabled");
    } else {
      this.addDropButton.setAttribute("disabled", "true");
    }
  }

  updateAddDropsButton(isValid) {
    if (isValid) {
      this.addDropsButton.removeAttribute("disabled");
    } else {
      this.addDropsButton.setAttribute("disabled", "true");
    }
  }

  /*
    Validates Drop Calculator user added drops against total selected fabric length
  */
  validateDrops(totalLength) {
    const drops = this.dropsContainer.querySelectorAll(`[${selectors.DROP_GROUP}]`);
    let dropsString = "";

    const dropsTotal = Array.from(drops).reduce((acc, drop) => {
      const dropsQty = drop.querySelector(`[${selectors.DROPS_QTY}]`).value;
      const dropsLength = drop.querySelector(`[${selectors.DROPS_LENGTH}]`).value;
      dropsString += `${dropsQty}x${dropsLength}m,`;
      return Math.round((acc + dropsQty * dropsLength) * 10) / 10;
    }, 0.0);

    const allDropsHaveValues = Array.from(drops).every((drop) => {
      return (
        drop.querySelector(`[${selectors.DROPS_QTY}]`).value > 0 &&
        drop.querySelector(`[${selectors.DROPS_LENGTH}]`).value > 0
      );
    });

    this.updateAddDropButton({ allDropsHaveValues, dropsTotal, totalLength });
    this.updateAddDropsButton(dropsTotal === totalLength);
    this.updateMetersRemainingLabel({ totalLength: totalLength, dropsTotal: dropsTotal });

    if (dropsTotal === totalLength) {
      this.generateDropsLineItemProp(dropsString);
    } else {
      this.removeDropsInput();
    }

    return dropsTotal === totalLength;
  }

  removeDropsInput() {
    const dropsInput = document.querySelector(`[${selectors.DROPS_LINE_ITEM_PROP}]`);
    if (dropsInput) {
      dropsInput.remove();
    }
  }

  generateDropsLineItemProp(dropsString) {
    const cleanedString = dropsString.slice(0, -1);
    const dropsInput = `<input id="drops" type="hidden" name="properties[Drops]" value="${cleanedString}" ${selectors.DROPS_LINE_ITEM_PROP}>`;
    const mainProductForm = document.querySelector(selectors.MAIN_PRODUCT_FORM);
    mainProductForm.insertAdjacentHTML("afterbegin", dropsInput);
  }

  deleteDropsLineItemProp() {
    const dropsInput = document.querySelector(`[${selectors.DROPS_LINE_ITEM_PROP}]`);
    if (dropsInput) {
      dropsInput.remove();
    }
  }

  openDropCalculator() {
    this.dialog.show();
    this.validateDrops(this.totalLength);
    document.body.classList.add("modal-show");
  }

  closeAndReset() {
    this?.dropsContainer?.querySelectorAll("[data-cloned-drop]").forEach((drop) => drop.remove());
    this?.dropsContainer?.querySelectorAll("input").forEach((input) => (input.value = ""));
    this.removeDropsInput();
    this.deleteDropsLineItemProp();
    document.body.classList.remove("modal-show");
    this.destroy();
  }

  destroy() {
    this?.abortController?.abort();
    this.dialog.close();
  }
}

customElements.define("cp-drop-calculator", CPDropCalculator);

class CPProductSpecsTable extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const jsonEl = this.querySelector('script[type="application/json"][data-specs]');
    this.variantData = JSON.parse(jsonEl.textContent) || [];

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
      this.handleVariantChange(variant);
    });
  }

  handleVariantChange(variant) {
    const variantId = parseInt(variant?.id);
    const currentVariantData = this.variantData?.find((variant) => parseInt(variant.id) === variantId);

    const specsHtml = currentVariantData.specs
      .map((spec) => `<div class="cp-product-specs-table__item"><strong>${spec.label}:</strong> ${spec.value}</div>`)
      .join("");

    document.querySelector("cp-product-specs-table").innerHTML = specsHtml;
  }
}

customElements.define("cp-product-specs-table", CPProductSpecsTable);

class CPProductCareInstructions extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const jsonEl = this.querySelector('script[type="application/json"][data-product-care-instructions]');
    try {
      this.variantData = JSON.parse(jsonEl?.textContent?.trim() || "[]");
    } catch {
      this.variantData = [];
    }

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
      this.handleVariantChange(variant);
    });
  }

  handleVariantChange(variant) {
    const variantId = parseInt(variant?.id);
    const currentVariantData = this.variantData?.find((variant) => parseInt(variant.id) === variantId);

    document.querySelector("[data-product-care-code]").textContent = currentVariantData?.variant_care_code ?? "";
    document.querySelector("[data-product-care-instructions]").innerHTML = (
      currentVariantData?.variant_care_instructions ?? []
    )
      .map((instruction) => `<li>${instruction.icon ? instruction.icon : ""}${instruction.label}</li>`)
      .join("");
  }
}

customElements.define("product-care-instructions", CPProductCareInstructions);

class CPStockIndicator extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const jsonEl = document.querySelector('script[type="application/json"][data-cp-variants]');
    this.variantData = JSON.parse(jsonEl.textContent) || [];
    this.classList.add("is-initialised");
  }

  updateStockIndicator({ unitOfMeasure, variantId }) {
    const variantData = this.variantData.find((v) => Number(v.id) === Number(variantId));
    if (!variantData) return;

    if (unitOfMeasure) {
      if (unitOfMeasure === UOM_METER) {
        this.querySelector("[data-stock-levels]").textContent = variantData.inventory_quantity_meters;
        this.querySelector("[data-stock-unit]").textContent = "m";
      } else {
        this.querySelector("[data-stock-levels]").textContent = variantData.max_rolls;
        this.querySelector("[data-stock-unit]").textContent = " rolls";
      }
    } else {
      this.querySelector("[data-stock-levels]").textContent = variantData.inventory_quantity;
      this.querySelector("[data-stock-unit]").textContent = "";
    }

    const continueSellingEl = this.querySelector("[data-continue-selling-message]");
    if (continueSellingEl) {
      const qty = Number(variantData.inventory_quantity);
      const policyContinue = variantData.inventory_policy === "continue";
      const showContinueSelling = policyContinue;
      if (showContinueSelling) {
        continueSellingEl.classList.add("is-visible");
      } else {
        continueSellingEl.classList.remove("is-visible");
      }
    }
  }
}

customElements.define("cp-stock-indicator", CPStockIndicator);

class CPDescription extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const jsonEl = this.querySelector('script[type="application/json"][data-cp-description]');
    this.variantData = JSON.parse(jsonEl.textContent) || [];

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
      this.handleVariantChange(variant);
    });
  }

  handleVariantChange(variant) {
    const variantId = parseInt(variant?.id);
    const currentVariantData = this.variantData?.find((variant) => parseInt(variant.id) === variantId);
    if (this.querySelector("[data-cp-description-text]")) {
      this.querySelector("[data-cp-description-text]").textContent = currentVariantData?.description;
    }
  }
}

customElements.define("cp-description", CPDescription);

class CPHandlingFeeUI extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addButton = this.querySelector("[data-handling-fee-add]");
    if (!this.addButton) return;

    this.variantId = this.dataset.handlingFeeVariantId;
    this.sectionId = this.dataset.sectionId;
    if (!this.variantId) return;

    this.addButton.addEventListener("click", this.addHandlingFee.bind(this));
  }

  addHandlingFee() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } }),
    );

    const body = JSON.stringify({
      items: [{ id: Number(this.variantId), quantity: 1 }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    this.addButton.classList.add("btn--loading");
    this.addButton.setAttribute("disabled", true);

    fetch(FoxTheme.routes.cart_add_url, { ...FoxTheme.utils.fetchConfig("javascript"), body })
      .then((res) => res.json())
      .then(async (parsed) => {
        if (parsed.status) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: "handling-fee",
            errors: parsed.errors || parsed.description,
            message: parsed.message,
          });
          return;
        }
        const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson.sections = parsed.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) cartDrawer.show(this.addButton);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        this.addButton?.classList.remove("btn--loading");
        this.addButton?.removeAttribute("disabled");
      });
  }
}

customElements.define("cp-handling-fee-ui", CPHandlingFeeUI);

class CPRegistrationForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.billingCountrySelect = this.querySelector("[data-registration-billing-country]");
    this.billingCity = this.querySelector("[data-registration-billing-city]");
    this.billingState = this.querySelector("[data-registration-billing-state]");

    this.deliveryCountrySelect = this.querySelector("[data-registration-delivery-country]");
    this.deliveryCity = this.querySelector("[data-registration-delivery-city]");
    this.deliveryState = this.querySelector("[data-registration-delivery-state]");

    this.useBillingAsDeliveryCheckbox = this.querySelector("[data-registration-use-billing]");
    this.deliveryFields = this.querySelector("[data-registration-delivery-fields]");

    this.deliveryCountrySelect.addEventListener("change", (event) =>
      this.handleCountryChange(event, this.deliveryCity, this.deliveryState),
    );
    this.billingCountrySelect.addEventListener("change", (event) =>
      this.handleCountryChange(event, this.billingCity, this.billingState),
    );

    this.useBillingAsDeliveryCheckbox.addEventListener("change", (event) => this.toggleDeliveryFields(event));
  }

  handleCountryChange(event, cityField, stateField) {
    const country = event.target.value;
    if (country === "New Zealand") {
      cityField.hidden = false;
      stateField.hidden = true;
      stateField.querySelector("select").disabled = true;
    } else {
      cityField.hidden = true;
      stateField.hidden = false;
      stateField.querySelector("select").disabled = false;
    }
  }

  toggleDeliveryFields(event) {
    this.deliveryFields.hidden = !!event.target.checked;
    this.deliveryFields.querySelectorAll("input, select").forEach((field) => (field.disabled = !!event.target.checked));
  }
}

customElements.define("cp-registration-form", CPRegistrationForm);

class CPProductEnquiryMessage extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.formMessage = document.querySelector("[data-product-enquiry-form-message]");
    this.formError = document.querySelector("[data-product-enquiry-form-error]");

    this.handleFormMessages();
  }

  handleFormMessages() {
    if (this.formMessage) {
      this.innerHTML = this.formMessage.innerHTML;
      this.hidden = false;
      this.formMessage.hidden = true;
    }
    if (this.formError) {
      this.innerHTML = this.formError.innerHTML;
      this.hidden = false;
      this.formError.hidden = true;
    }
  }
}

customElements.define("cp-product-enquiry-message", CPProductEnquiryMessage);

class CPProductEnquiryForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.productTitle = this.querySelector("[data-product-enquiry-product]");

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
      this.productTitle.value = variant?.name;
    });
  }
}

customElements.define("cp-product-enquiry-form", CPProductEnquiryForm);

// Handle ATC button labels & preorder message visibility
const handleVariantAvailability = (variantId) => {
  const atcButtonReal = document.querySelector("[data-real-atc]");
  const atcButtonsCustom = document.querySelectorAll("[data-custom-atc]");
  const jsonEl = document.querySelector('script[type="application/json"][data-cp-variants]');

  if (!atcButtonReal || !jsonEl) return;

  const variantData = JSON.parse(jsonEl.textContent) || [];
  const variant = variantData.find((v) => parseInt(v.id, 10) === parseInt(variantId, 10));
  if (!variant) return;

  const qty = Number(variant.inventory_quantity);
  const policyContinue = variant.inventory_policy === "continue";

  let label;
  let disabled = false;

  if (qty <= 0 && policyContinue) {
    label = atcButtonReal.dataset.preorderLabel;
  } else if (qty <= 0) {
    label = atcButtonReal.dataset.soldOutLabel;
    disabled = true;
  } else {
    // if Roll Only UI, test if we have enough QTY to make a roll
    const customQtyElement = document.querySelector("cp-custom-quantity");
    const isRollOnlyUi = customQtyElement?.hasAttribute("data-roll-only-ui");
    if (customQtyElement && isRollOnlyUi) {
      const max_rolls = Number(variant.max_rolls);
      if (max_rolls > 0) {
        label = atcButtonReal.dataset.atcLabel;
      } else {
        label = atcButtonReal.dataset.soldOutLabel;
        disabled = true;
      }
    } else {
      label = atcButtonReal.dataset.atcLabel;
    }
  }

  const btnText = atcButtonReal.querySelector(".btn__text");
  if (btnText) btnText.textContent = label;
  atcButtonReal.disabled = disabled;

  atcButtonsCustom.forEach((btn) => {
    btn.textContent = label;
    btn.disabled = disabled;
  });

  const preorderMessage = document.querySelector("[data-preorder-message]");
  if (preorderMessage) {
    if (variant.preorder_date) {
      const preorderTitle = preorderMessage.dataset.preorderTitle;
      const titleTarget = preorderMessage.querySelector("[data-title]");
      const preorderContent = preorderMessage.dataset.preorderContent;
      const contentTarget = preorderMessage.querySelector("[data-content]");
      const formattedDate = new Date(variant.preorder_date).toLocaleDateString("en-AU", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      titleTarget.textContent = preorderTitle.replace("||DATE||", formattedDate);
      contentTarget.textContent = preorderContent.replace("||DATE||", formattedDate);
      preorderMessage.classList.add("is-visible");
      preorderMessage.querySelector("input")?.remove();
      const lineItemMarkup = `<input type="hidden" name="properties[Ships from]" value="${formattedDate}" data-lineitemprop>`;
      preorderMessage.insertAdjacentHTML("beforeend", lineItemMarkup);
    } else {
      preorderMessage.classList.remove("is-visible");
      preorderMessage.querySelector("input")?.remove();
    }
  }
};

FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
  if (variant?.id) handleVariantAvailability(variant.id);
  // If using default QTY inputs, still need to update stock indicator
  if (isProductPage) {
    const customQtyInputs = document.querySelectorAll("cp-custom-quantity");
    if (customQtyInputs.length === 0) {
      const stockIndicator = document.querySelector("cp-stock-indicator");
      if (stockIndicator) {
        stockIndicator.updateStockIndicator({ unitOfMeasure: null, variantId: variant.id });
      }
    }
  }
});

/* 
  Dynamically update pricess on PDP as the QTY changes (B2B only) 
  Extends "Real QTY Input" to observe value changes (manual & programmatic) and call callback function.
*/

function observeInputValue(input, callback) {
  const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value");
  let lastNotified = input.value;

  const notify = (val) => {
    const qty = parseInt(val, 10);
    if (String(val) !== String(lastNotified)) {
      lastNotified = input.value;
      setTimeout(() => {
        callback(isNaN(qty) ? val : qty);
      }, 5);
    }
  };

  Object.defineProperty(input, "value", {
    get: () => descriptor.get.call(input),
    set(val) {
      descriptor.set.call(input, val);
      notify(val);
    },
    configurable: true,
    enumerable: true,
  });

  input.addEventListener("change", () => notify(input.value));
}

if (isProductPage && window.CharlesParsons.isB2B) {
  const defaultQtyInput = document.querySelector(".product__info-wrapper .default-qty-buy .quantity__input");
  if (defaultQtyInput) {
    observeInputValue(defaultQtyInput, (qty) => {
      const variantId = defaultQtyInput.closest("form").querySelector("input[name='id']").value;
      const variantsData = JSON.parse(
        document.querySelector('script[type="application/json"][data-cp-variants]').textContent || [],
      );
      // Need to check if variant has volume pricing and match qty to the highest applicable price break
      const variant = variantsData.find((v) => v.id === variantId);
      if (variant?.volume_pricing) {
        const customQtyElement = defaultQtyInput.closest("form").querySelector("cp-custom-quantity");
        const unitOfMeasure = customQtyElement?.getAttribute("data-selected-unit-of-measure");

        const matchingBreaks = variant.volume_pricing.filter((p) => Number(qty) >= Number(p.quantity));
        const priceBreak = matchingBreaks.length
          ? matchingBreaks.reduce((best, p) => (!best || Number(p.quantity) > Number(best.quantity) ? p : best), null)
          : variant.volume_pricing.reduce(
              (lowest, p) => (!lowest || Number(p.quantity) < Number(lowest.quantity) ? p : lowest),
              null,
            );
        if (priceBreak) {
          // if custom UI, need to check unit of measure and update price accordingly
          const price = priceBreak.price;
          const priceElement = document.querySelector("[data-cp-price]");
          const priceTarget = priceElement.querySelector("[data-price-regular]");
          const currency = priceElement.dataset.currency;

          if (customQtyElement) {
            // Either Meters or Rolls, handle accordingly
            if (unitOfMeasure === UOM_METER) {
              const pricePerMeterInteger = Number(price) * 10;
              const formattedPrice = window.FoxTheme.Currency.formatMoney(pricePerMeterInteger);
              const priceMarkup = `
                ${formattedPrice} ${currency} <span data-price-unit-text>${priceElement.dataset.perMeterText}</span>
              `;
              priceTarget.innerHTML = priceMarkup;
            } else {
              const rollPrice = Number(variant.roll_length) * Number(price);
              const formattedPrice = window.FoxTheme.Currency.formatMoney(rollPrice);
              const priceMarkup = `
                ${formattedPrice} ${currency} <span data-price-unit-text>${priceElement.dataset.rollText}</span>
              `;
              priceTarget.innerHTML = priceMarkup;
            }
          } else {
            // Fallback in case they have price breaks on non-meter products...
            const formattedPrice = window.FoxTheme.Currency.formatMoney(price);
            const priceMarkup = `${formattedPrice}`;
            priceTarget.innerHTML = priceMarkup;
          }
        }
      }
    });
  }
}
